import React, {useEffect, useMemo, useState} from 'react';
import {AuthUser} from '../../../../services/authApi';
import {
  enterpriseApi,
  EnterpriseInviteRecord,
  EnterpriseInviteRole,
  EnterpriseMemberRecord,
  EnterpriseMemberStatus,
  EnterpriseMeResponse,
} from '../../../../services/enterpriseApi';
import styles from '../Settings.module.css';

interface SettingsEnterpriseSectionProps {
  token: string | null;
  currentUser: AuthUser | null;
}

const ENTERPRISE_ROLE_OPTIONS: EnterpriseInviteRole[] = ['admin', 'coach', 'recruiter', 'staff'];
const MEMBER_STATUS_OPTIONS: EnterpriseMemberStatus[] = ['active', 'inactive'];

const formatRoleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);
const formatDateLabel = (value?: string) => (value ? new Date(value).toLocaleString() : 'N/A');

const SettingsEnterpriseSection: React.FC<SettingsEnterpriseSectionProps> = ({
  token,
  currentUser,
}) => {
  const enterpriseSummary = currentUser?.enterprise || null;
  const canManageSeats = Boolean(enterpriseSummary?.canManageSeats);
  const [enterpriseAccount, setEnterpriseAccount] = useState<EnterpriseMeResponse | null>(null);
  const [members, setMembers] = useState<EnterpriseMemberRecord[]>([]);
  const [invites, setInvites] = useState<EnterpriseInviteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<EnterpriseInviteRole>('coach');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [memberDrafts, setMemberDrafts] = useState<Record<string, {role: EnterpriseInviteRole; status: EnterpriseMemberStatus}>>({});
  const [memberSavingId, setMemberSavingId] = useState<string | null>(null);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const resetActionState = () => {
    setActionMessage(null);
    setActionError(null);
  };

  const upsertInvite = (invite: EnterpriseInviteRecord) => {
    setInvites((currentInvites) => {
      const nextInvites = currentInvites.filter((currentInvite) => currentInvite.id !== invite.id);
      return [invite, ...nextInvites];
    });
  };

  useEffect(() => {
    if (!token || !enterpriseSummary) {
      setEnterpriseAccount(null);
      setMembers([]);
      setInvites([]);
      setError(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    const loadEnterpriseData = async () => {
      try {
        const enterpriseMe = await enterpriseApi.getMe(token);

        if (!isMounted) {
          return;
        }

        setEnterpriseAccount(enterpriseMe);

        if (canManageSeats) {
          const [enterpriseMembers, enterpriseInvites] = await Promise.all([
            enterpriseApi.getMembers(token),
            enterpriseApi.getInvites(token),
          ]);

          if (!isMounted) {
            return;
          }

          setMembers(enterpriseMembers);
          setInvites(enterpriseInvites);
        }
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Failed to load Enterprise account.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadEnterpriseData();

    return () => {
      isMounted = false;
    };
  }, [token, enterpriseSummary, canManageSeats]);

  const activeMembers = useMemo(
    () => members.filter((member) => member.status === 'active').length,
    [members]
  );

  if (!enterpriseSummary) {
    return null;
  }

  const handleCreateInvite = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!token) {
      setActionError('You must be signed in to invite Enterprise members.');
      return;
    }

    resetActionState();
    setInviteSubmitting(true);

    try {
      const invite = await enterpriseApi.createInvite(token, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      upsertInvite(invite);
      setInviteEmail('');
      setInviteRole('coach');

      if (invite.inviteUrl) {
        setActionMessage(`Invite created. Share this link with ${invite.email}: ${invite.inviteUrl}`);
      } else {
        setActionMessage(`Invite created for ${invite.email}.`);
      }
    } catch (inviteError) {
      setActionError(inviteError instanceof Error ? inviteError.message : 'Failed to create Enterprise invite.');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCopyInviteLink = async (inviteUrl?: string) => {
    if (!inviteUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteUrl);
      setActionMessage('Invite link copied to your clipboard.');
      setActionError(null);
    } catch (clipboardError) {
      setActionError('We could not copy the invite link automatically. Please copy it manually from the screen.');
    }
  };

  const handleSaveMember = async (member: EnterpriseMemberRecord) => {
    if (!token || member.role === 'owner') {
      return;
    }

    const draft = memberDrafts[member.id] || {
      role: member.role as EnterpriseInviteRole,
      status: member.status,
    };

    resetActionState();
    setMemberSavingId(member.id);

    try {
      const updatedMember = await enterpriseApi.updateMember(token, member.id, draft);
      setMembers((currentMembers) => currentMembers.map((currentMember) => (
        currentMember.id === updatedMember.id ? updatedMember : currentMember
      )));
      setMemberDrafts((currentDrafts) => {
        const nextDrafts = {...currentDrafts};
        delete nextDrafts[member.id];
        return nextDrafts;
      });
      setActionMessage(`Updated ${updatedMember.name}'s Enterprise access.`);
    } catch (memberError) {
      setActionError(memberError instanceof Error ? memberError.message : 'Failed to update Enterprise member.');
    } finally {
      setMemberSavingId(null);
    }
  };

  const handleInviteAction = async (
    invite: EnterpriseInviteRecord,
    action: 'resend' | 'revoke'
  ) => {
    if (!token) {
      return;
    }

    resetActionState();
    setInviteActionId(invite.id);

    try {
      const updatedInvite = action === 'resend'
        ? await enterpriseApi.resendInvite(token, invite.id)
        : await enterpriseApi.revokeInvite(token, invite.id);

      upsertInvite(updatedInvite);

      if (action === 'resend' && updatedInvite.inviteUrl) {
        setActionMessage(`Invite link refreshed for ${updatedInvite.email}: ${updatedInvite.inviteUrl}`);
      } else {
        setActionMessage(
          action === 'resend'
            ? `Invite resent to ${updatedInvite.email}.`
            : `Invite revoked for ${updatedInvite.email}.`
        );
      }
    } catch (inviteError) {
      setActionError(inviteError instanceof Error ? inviteError.message : 'Failed to update Enterprise invite.');
    } finally {
      setInviteActionId(null);
    }
  };

  return (
    <section className={`card ${styles.settingsCard}`}>
      <div className={styles.enterpriseHeader}>
        <div>
          <h3 className={styles.settingsCardTitle}>Enterprise Account</h3>
          <p className={styles.enterpriseSummaryText}>
            {enterpriseSummary.organizationName} · {formatRoleLabel(enterpriseSummary.role)}
          </p>
        </div>

        {enterpriseAccount ? (
          <div className={styles.enterpriseUsageBadge}>
            {enterpriseAccount.organization.activeSeatCount}/{enterpriseAccount.organization.seatLimit} seats
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className={styles.enterpriseSummaryText}>Loading your Enterprise account…</p>
      ) : null}

      {error ? (
        <p className={styles.appearanceErrorText}>{error}</p>
      ) : null}

      {enterpriseAccount ? (
        <div className={styles.enterpriseMetaGrid}>
          <div className={styles.enterpriseMetaCard}>
            <span className={styles.enterpriseMetaLabel}>Organization</span>
            <strong>{enterpriseAccount.organization.name}</strong>
          </div>
          <div className={styles.enterpriseMetaCard}>
            <span className={styles.enterpriseMetaLabel}>Role</span>
            <strong>{formatRoleLabel(enterpriseAccount.membership.role)}</strong>
          </div>
          <div className={styles.enterpriseMetaCard}>
            <span className={styles.enterpriseMetaLabel}>Permissions</span>
            <strong>
              {enterpriseAccount.membership.canManageSeats
                ? 'Manage seats'
                : enterpriseAccount.membership.canRecruit
                  ? 'Recruiting'
                  : 'Read-only'}
            </strong>
          </div>
          <div className={styles.enterpriseMetaCard}>
            <span className={styles.enterpriseMetaLabel}>Pending Invites</span>
            <strong>{enterpriseAccount.organization.pendingInviteCount}</strong>
          </div>
        </div>
      ) : null}

      {!canManageSeats ? (
        <p className={styles.enterpriseSummaryText}>
          Your Enterprise seat is active. Owners and admins manage members and invites from this screen.
        </p>
      ) : null}

      {actionMessage ? (
        <div className={`${styles.billingStatusMessage} ${styles.billingStatusMessageSuccess}`}>
          {actionMessage}
        </div>
      ) : null}

      {actionError ? (
        <div className={`${styles.billingStatusMessage} ${styles.billingStatusMessageError}`}>
          {actionError}
        </div>
      ) : null}

      {canManageSeats ? (
        <>
          <form onSubmit={handleCreateInvite} className={styles.enterpriseInviteForm}>
            <div className={styles.enterpriseFormField}>
              <label htmlFor="enterprise-invite-email" className={styles.appearanceBlockLabel}>Invite by Email</label>
              <input
                id="enterprise-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                className={styles.enterpriseInput}
                placeholder="coach@college.edu"
                required
              />
            </div>

            <div className={styles.enterpriseFormField}>
              <label htmlFor="enterprise-invite-role" className={styles.appearanceBlockLabel}>Role</label>
              <select
                id="enterprise-invite-role"
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value as EnterpriseInviteRole)}
                className={styles.settingsSelect}
              >
                {ENTERPRISE_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {formatRoleLabel(role)}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className={styles.enterprisePrimaryButton}
              disabled={inviteSubmitting}
            >
              {inviteSubmitting ? 'Creating Invite…' : 'Create Invite'}
            </button>
          </form>

          <div className={styles.enterpriseSubsection}>
            <div className={styles.enterpriseSubsectionHeader}>
              <h4 className={styles.enterpriseSubsectionTitle}>Members</h4>
              <span className={styles.enterpriseSubsectionMeta}>
                {activeMembers} active
              </span>
            </div>

            <div className={styles.enterpriseList}>
              {members.map((member) => {
                const draft = member.role === 'owner'
                  ? null
                  : memberDrafts[member.id] || {
                    role: member.role as EnterpriseInviteRole,
                    status: member.status,
                  };
                const hasDraftChanges = Boolean(
                  draft && (draft.role !== member.role || draft.status !== member.status)
                );

                return (
                  <div key={member.id} className={styles.enterpriseListRow}>
                    <div>
                      <div className={styles.enterpriseMemberName}>{member.name}</div>
                      <div className={styles.enterpriseMemberMeta}>
                        {member.email} · Joined {formatDateLabel(member.joinedAt || member.createdAt)}
                      </div>
                    </div>

                    <div className={styles.enterpriseMemberControls}>
                      <span className={`${styles.enterpriseRoleBadge} ${member.status === 'inactive' ? styles.enterpriseRoleBadgeMuted : ''}`}>
                        {formatRoleLabel(member.role)}
                      </span>

                      {member.role === 'owner' ? (
                        <span className={styles.enterpriseControlHint}>Owner seat</span>
                      ) : (
                        <>
                          <select
                            value={draft?.role || member.role}
                            onChange={(event) => setMemberDrafts((currentDrafts) => ({
                              ...currentDrafts,
                              [member.id]: {
                                role: event.target.value as EnterpriseInviteRole,
                                status: draft?.status || member.status,
                              },
                            }))}
                            className={styles.settingsSelect}
                          >
                            {ENTERPRISE_ROLE_OPTIONS.map((role) => (
                              <option key={role} value={role}>
                                {formatRoleLabel(role)}
                              </option>
                            ))}
                          </select>

                          <select
                            value={draft?.status || member.status}
                            onChange={(event) => setMemberDrafts((currentDrafts) => ({
                              ...currentDrafts,
                              [member.id]: {
                                role: draft?.role || (member.role as EnterpriseInviteRole),
                                status: event.target.value as EnterpriseMemberStatus,
                              },
                            }))}
                            className={styles.settingsSelect}
                          >
                            {MEMBER_STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {formatRoleLabel(status)}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className={styles.enterpriseSecondaryButton}
                            disabled={!hasDraftChanges || memberSavingId === member.id}
                            onClick={() => void handleSaveMember(member)}
                          >
                            {memberSavingId === member.id ? 'Saving…' : 'Save'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.enterpriseSubsection}>
            <div className={styles.enterpriseSubsectionHeader}>
              <h4 className={styles.enterpriseSubsectionTitle}>Invites</h4>
              <span className={styles.enterpriseSubsectionMeta}>{invites.length} total</span>
            </div>

            <div className={styles.enterpriseList}>
              {invites.map((invite) => (
                <div key={invite.id} className={styles.enterpriseListRow}>
                  <div>
                    <div className={styles.enterpriseMemberName}>{invite.email}</div>
                    <div className={styles.enterpriseMemberMeta}>
                      {formatRoleLabel(invite.role)} · {formatRoleLabel(invite.status)} · Expires {formatDateLabel(invite.expiresAt)}
                    </div>
                  </div>

                  <div className={styles.enterpriseInviteActions}>
                    {invite.inviteUrl ? (
                      <button
                        type="button"
                        className={styles.enterpriseSecondaryButton}
                        onClick={() => void handleCopyInviteLink(invite.inviteUrl)}
                      >
                        Copy Link
                      </button>
                    ) : null}

                    {invite.status === 'pending' || invite.status === 'expired' ? (
                      <button
                        type="button"
                        className={styles.enterpriseSecondaryButton}
                        onClick={() => void handleInviteAction(invite, 'resend')}
                        disabled={inviteActionId === invite.id}
                      >
                        {inviteActionId === invite.id ? 'Working…' : 'Resend'}
                      </button>
                    ) : null}

                    {invite.status === 'pending' ? (
                      <button
                        type="button"
                        className={styles.enterpriseDangerButton}
                        onClick={() => void handleInviteAction(invite, 'revoke')}
                        disabled={inviteActionId === invite.id}
                      >
                        {inviteActionId === invite.id ? 'Working…' : 'Revoke'}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default React.memo(SettingsEnterpriseSection);
